import type { AiPreference } from "../../shared/connect-ai";
import { useState, useEffect, useCallback } from "react";
import { getStoredAuth } from "../auth";

interface OnboardingState {
  completed: boolean;
  completedAt: string | null;
  phase: "setup" | "tour" | "complete";
  currentStep: string;
  stepsCompleted: string[];
  skippedSteps: string[];
  preset: string | null;
  agentName: string;
  answers: {
    agentName: string;
    dayOneFocus: string | null;
    aiPreference: AiPreference;
    aiMode: AiPreference;
    connectedAccounts: string[];
    budgetPreference: string | null;
    limitedModeConfirmed: boolean;
  };
  tourProgress: {
    currentStep: string | null;
    completedSteps: string[];
    skippedSteps: string[];
  };
  startedAt: string;
}

interface OnboardingBlocker {
  reason: string;
  message: string;
}

function onboardingSkipKey(): string {
  const auth = getStoredAuth();
  const identity = auth.sub ?? auth.email ?? "anonymous";
  return `agentme-onboarding-skipped:${identity}`;
}

function readPersistedOnboardingSkip(): boolean {
  try {
    return localStorage.getItem(onboardingSkipKey()) === "true";
  } catch {
    return false;
  }
}

function onboardingCompleteKey(): string {
  const auth = getStoredAuth();
  const identity = auth.sub ?? auth.email ?? "anonymous";
  return `agentme-onboarding-complete:${identity}`;
}

function readPersistedOnboardingComplete(): boolean {
  try {
    return localStorage.getItem(onboardingCompleteKey()) === "true";
  } catch {
    return false;
  }
}

function persistOnboardingComplete(): void {
  try {
    localStorage.setItem(onboardingCompleteKey(), "true");
  } catch {
    // localStorage unavailable
  }
}

export function useOnboarding(enabled = true) {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [skipped, setSkipped] = useState(() => readPersistedOnboardingSkip());
  const [blocker, setBlocker] = useState<OnboardingBlocker | null>(null);

  const fetchState = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      setBlocker(null);
      return;
    }
    // Fast path: if localStorage says completed, trust it — avoids server round-trip
    // and survives memory store restarts
    if (readPersistedOnboardingComplete()) {
      setState(
        (prev) =>
          prev ?? {
            completed: true,
            completedAt: null,
            phase: "complete" as const,
            currentStep: "complete",
            stepsCompleted: [],
            skippedSteps: [],
            preset: null,
            agentName: "Agent Me",
            answers: {
              agentName: "Agent Me",
              dayOneFocus: null,
              aiPreference: "workspace_default" as AiPreference,
              aiMode: "workspace_default" as AiPreference,
              connectedAccounts: [],
              budgetPreference: null,
              limitedModeConfirmed: false,
            },
            tourProgress: { currentStep: null, completedSteps: [], skippedSteps: [] },
            startedAt: new Date().toISOString(),
          },
      );
      setLoading(false);
      setBlocker(null);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/onboarding");
      if (res.ok) {
        const data = await res.json();
        // Persist completion to localStorage so it survives store resets
        if (data.completed) persistOnboardingComplete();
        setState(data);
        setBlocker(null);
        return;
      }
      if (res.status === 401) {
        setState(null);
        setBlocker(null);
        return;
      }
      const errorBody = (await res.json().catch(() => null)) as {
        reason?: string;
        message?: string;
      } | null;
      setState(null);
      setBlocker({
        reason: errorBody?.reason ?? `http_${res.status}`,
        message: errorBody?.message ?? "Setup is not ready yet. Please try again in a moment.",
      });
    } catch {
      setState(null);
      setBlocker({
        reason: "network_unavailable",
        message: "Setup could not connect right now. Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setBlocker(null);
      return;
    }
    setSkipped(readPersistedOnboardingSkip());
    fetchState();
  }, [enabled, fetchState]);

  const skipOnboarding = useCallback(() => {
    try {
      localStorage.setItem(onboardingSkipKey(), "true");
    } catch {
      // localStorage unavailable
    }
    setSkipped(true);
  }, []);

  const needsOnboarding = !loading && state !== null && !state.completed && !skipped;

  return { state, loading, needsOnboarding, blocker, skipOnboarding, refetch: fetchState };
}
